import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { Metadata, RunView, CompositeKey } from '@memberjunction/core';
import { Subject } from 'rxjs';
import { MJCredentialTypeEntity } from '@memberjunction/core-entities';
import { IAdminPanel, AdminPanelStatus, DEFAULT_ADMIN_PANEL_STATUS } from '../admin-panel.interface';

interface CredentialTypeDisplay {
  credentialType: MJCredentialTypeEntity;
  isEditing: boolean;
  editForm: CredentialTypeEditForm;
  parsedSchema: Record<string, any> | null;
}

interface CredentialTypeEditForm {
  name: string;
  description: string;
  category: 'AI' | 'Authentication' | 'Communication' | 'Database' | 'Integration' | 'Storage';
  fieldSchema: string;
}

/**
 * Credential Types Management Component
 *
 * Provides admin interface for managing credential types:
 * - Define credential schemas for different vendors (HubSpot, Twilio, etc.)
 * - JSON Schema-based field definitions for dynamic forms
 * - Category organization for easy discovery
 *
 * This is an admin-level component, typically used by system administrators
 * to configure the available credential types.
 */
@Component({
  standalone: false,
  selector: 'izzy-credential-types',
  templateUrl: './credential-types.component.html',
  styleUrls: ['./credential-types.component.css']
})
export class CredentialTypesComponent implements OnInit, OnDestroy, IAdminPanel {

  @Input() initialState: any = null;
  @Output() stateChange = new EventEmitter<any>();
  @Output() statusChange = new EventEmitter<AdminPanelStatus>();
  @Output() openRecord = new EventEmitter<{ EntityName: string; RecordPKey: CompositeKey }>();

  constructor() {}

  // Data
  protected credentialTypes: MJCredentialTypeEntity[] = [];
  protected credentialTypeDisplays: CredentialTypeDisplay[] = [];

  // UI State
  protected isLoading: boolean = true;
  protected isSaving: boolean = false;
  protected errorMessage: string = '';
  protected successMessage: string = '';
  protected searchQuery: string = '';
  protected selectedCategory: string = '';

  // Create Mode
  protected isCreating: boolean = false;
  protected createForm: CredentialTypeEditForm = this.getEmptyForm();

  // Confirm Dialog
  protected confirmDialogVisible: boolean = false;
  protected confirmDialogTitle: string = '';
  protected confirmDialogMessage: string = '';
  protected confirmDialogAction: (() => Promise<void>) | null = null;

  private destroy$ = new Subject<void>();

  async ngOnInit(): Promise<void> {
    await this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getEmptyForm(): CredentialTypeEditForm {
    return {
      name: '',
      description: '',
      category: 'AI',
      fieldSchema: this.getDefaultSchema()
    };
  }

  private getDefaultSchema(): string {
    return JSON.stringify({
      "$schema": "http://json-schema.org/draft-07/schema#",
      "type": "object",
      "properties": {
        "apiKey": {
          "type": "string",
          "title": "API Key",
          "description": "The API key for authentication"
        }
      },
      "required": ["apiKey"]
    }, null, 2);
  }

  protected async loadData(): Promise<void> {
    this.isLoading = true;
    this.errorMessage = '';

    try {
      const rv = new RunView();
      const result = await rv.RunView<MJCredentialTypeEntity>({
        EntityName: 'MJ: Credential Types',
        OrderBy: 'Category ASC, Name ASC'
      });

      this.credentialTypes = result.Success ? result.Results : [];
      this.credentialTypeDisplays = this.credentialTypes.map(ct => ({
        credentialType: ct,
        isEditing: false,
        editForm: this.credentialTypeToForm(ct),
        parsedSchema: this.parseSchema(ct.FieldSchema)
      }));

      // Emit default status (credential types don't have a health indicator)
      this.statusChange.emit(DEFAULT_ADMIN_PANEL_STATUS);
    } catch (error) {
      console.error('Error loading credential types:', error);
      this.errorMessage = 'Failed to load credential types.';
    } finally {
      this.isLoading = false;
    }
  }

  private credentialTypeToForm(ct: MJCredentialTypeEntity): CredentialTypeEditForm {
    return {
      name: ct.Name,
      description: ct.Description || '',
      category: ct.Category || 'AI',
      fieldSchema: ct.FieldSchema || this.getDefaultSchema()
    };
  }

  private parseSchema(schemaStr: string | null): Record<string, any> | null {
    if (!schemaStr) return null;
    try {
      return JSON.parse(schemaStr);
    } catch {
      return null;
    }
  }

  get categories(): string[] {
    const cats = new Set(this.credentialTypes.map(ct => ct.Category || 'Other'));
    return Array.from(cats).sort();
  }

  get filteredCredentialTypes(): CredentialTypeDisplay[] {
    return this.credentialTypeDisplays.filter(ctd => {
      const matchesSearch = !this.searchQuery.trim() ||
        ctd.credentialType.Name.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        (ctd.credentialType.Description && ctd.credentialType.Description.toLowerCase().includes(this.searchQuery.toLowerCase()));
      const matchesCategory = !this.selectedCategory ||
        ctd.credentialType.Category === this.selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }

  // Create Mode

  startCreate(): void {
    this.isCreating = true;
    this.createForm = this.getEmptyForm();
    this.cancelAllEdits();
  }

  cancelCreate(): void {
    this.isCreating = false;
    this.createForm = this.getEmptyForm();
  }

  validateSchema(schemaStr: string): boolean {
    try {
      const schema = JSON.parse(schemaStr);
      return schema && typeof schema === 'object' && schema.properties;
    } catch {
      return false;
    }
  }

  async saveCreate(): Promise<void> {
    if (!this.createForm.name.trim()) {
      this.errorMessage = 'Name is required.';
      return;
    }

    if (!this.validateSchema(this.createForm.fieldSchema)) {
      this.errorMessage = 'Field Schema must be valid JSON with a properties object.';
      return;
    }

    this.isSaving = true;
    this.errorMessage = '';

    try {
      const md = new Metadata();
      const newType = await md.GetEntityObject<MJCredentialTypeEntity>('MJ: Credential Types');

      newType.Name = this.createForm.name.trim();
      newType.Description = this.createForm.description.trim() || null;
      newType.Category = this.createForm.category as typeof newType.Category;
      newType.FieldSchema = this.createForm.fieldSchema;

      const saveResult = await newType.Save();
      if (!saveResult) {
        throw new Error('Failed to save credential type');
      }

      await this.loadData();
      this.showSuccess('Credential type created');
      this.cancelCreate();
    } catch (error) {
      console.error('Error creating credential type:', error);
      this.errorMessage = 'Failed to create credential type. Please try again.';
    } finally {
      this.isSaving = false;
    }
  }

  // Edit Mode

  startEdit(display: CredentialTypeDisplay): void {
    this.cancelAllEdits();
    this.cancelCreate();
    display.isEditing = true;
    display.editForm = this.credentialTypeToForm(display.credentialType);
  }

  cancelEdit(display: CredentialTypeDisplay): void {
    display.isEditing = false;
    display.editForm = this.credentialTypeToForm(display.credentialType);
  }

  cancelAllEdits(): void {
    for (const display of this.credentialTypeDisplays) {
      display.isEditing = false;
      display.editForm = this.credentialTypeToForm(display.credentialType);
    }
  }

  async saveEdit(display: CredentialTypeDisplay): Promise<void> {
    if (!this.validateSchema(display.editForm.fieldSchema)) {
      this.errorMessage = 'Field Schema must be valid JSON with a properties object.';
      return;
    }

    this.isSaving = true;
    this.errorMessage = '';

    try {
      const ct = display.credentialType;

      ct.Name = display.editForm.name.trim();
      ct.Description = display.editForm.description.trim() || null;
      ct.Category = display.editForm.category as typeof ct.Category;
      ct.FieldSchema = display.editForm.fieldSchema;

      const saveResult = await ct.Save();
      if (!saveResult) {
        throw new Error('Failed to save credential type');
      }

      display.parsedSchema = this.parseSchema(ct.FieldSchema);
      await this.loadData();
      this.showSuccess('Credential type updated');
    } catch (error) {
      console.error('Error updating credential type:', error);
      this.errorMessage = 'Failed to update credential type. Please try again.';
    } finally {
      this.isSaving = false;
    }
  }

  // Delete

  confirmDelete(display: CredentialTypeDisplay): void {
    this.showConfirmDialog(
      'Delete Credential Type',
      `Are you sure you want to delete "${display.credentialType.Name}"? This will prevent creating new credentials of this type.`,
      async () => {
        await this.deleteCredentialType(display);
      }
    );
  }

  private async deleteCredentialType(display: CredentialTypeDisplay): Promise<void> {
    this.isSaving = true;
    this.errorMessage = '';

    try {
      const result = await display.credentialType.Delete();
      if (!result) {
        throw new Error('Failed to delete credential type');
      }

      await this.loadData();
      this.showSuccess('Credential type deleted');
    } catch (error) {
      console.error('Error deleting credential type:', error);
      this.errorMessage = 'Failed to delete credential type. It may be in use by existing credentials.';
    } finally {
      this.isSaving = false;
    }
  }

  // Helpers

  getSchemaFieldCount(display: CredentialTypeDisplay): number {
    if (!display.parsedSchema?.properties) return 0;
    return Object.keys(display.parsedSchema.properties).length;
  }

  getSchemaFieldNames(display: CredentialTypeDisplay): string {
    if (!display.parsedSchema?.properties) return '';
    return Object.keys(display.parsedSchema.properties).join(', ');
  }

  getCategoryIcon(category: string): string {
    const cat = (category || '').toLowerCase();
    if (cat.includes('crm')) return 'fa-address-book';
    if (cat.includes('email')) return 'fa-envelope';
    if (cat.includes('sms')) return 'fa-comment-sms';
    if (cat.includes('oauth')) return 'fa-key';
    if (cat.includes('cloud')) return 'fa-cloud';
    return 'fa-code';
  }

  getStatusClass(status: string): string {
    return status === 'Active' ? 'status-active' : 'status-inactive';
  }

  formatJson(json: string): void {
    try {
      const parsed = JSON.parse(json);
      this.createForm.fieldSchema = JSON.stringify(parsed, null, 2);
    } catch {
      // Keep as-is if invalid
    }
  }

  openCredentialTypeRecord(display: CredentialTypeDisplay): void {
    this.openRecord.emit({
      EntityName: 'MJ: Credential Types',
      RecordPKey: CompositeKey.FromID(display.credentialType.ID)
    });
  }

  // Confirm Dialog

  private showConfirmDialog(title: string, message: string, action: () => Promise<void>): void {
    this.confirmDialogTitle = title;
    this.confirmDialogMessage = message;
    this.confirmDialogAction = action;
    this.confirmDialogVisible = true;
  }

  protected async confirmDialogYes(): Promise<void> {
    if (this.confirmDialogAction) {
      await this.confirmDialogAction();
    }
    this.closeConfirmDialog();
  }

  protected closeConfirmDialog(): void {
    this.confirmDialogVisible = false;
    this.confirmDialogAction = null;
  }

  private showSuccess(message: string): void {
    this.successMessage = message;
    setTimeout(() => { this.successMessage = ''; }, 3000);
  }
}

export function LoadCredentialTypesComponent(): void {}
